import ContentsToReact from '@/components/ContentsToReact/ContentsToReact';
import Page from '@/components/Page/Page';
import StructuredData from '@/components/StructuredData/StructuredData';
import createStructuredData from '@/helpers/createStructuredData/createStructuredData';
import { createEmploymentType } from '@/helpers/createStructuredData/helpers';
import extractPageMetaFromRouteData from '@/helpers/extractPageMetaFromRouteData';
import { useInitialPageDataLayer } from '@/hooks/useInitialPageDataLayer/useInitialPageDataLayer';
import { PageModel } from '@/models/page';
import EnercityFooter from '@uikit/components/EnercityFooter/EnercityFooter';
import JobDetailsPageContent, {
  getMatchingJobImage,
} from '@uikit/components/JobDetailsPageContent/JobDetailsPageContent';
import Layout from '@uikit/components/Layout/Layout';
import { CmsComponentsContext } from '../CmsComponentsContext';
import cmsComponents from './JobDetailsPage.cmsComponents';
import type { JobDetailsPageProps } from './interfaces';

const JobDetailsPage = (
  routeData: PageModel<JobDetailsPageProps>
): JSX.Element => {
  const {
    props: {
      jobCovers,
      jobBenefits,
      job,
      jobContacts,
      location,
      jobTimeTypeAdditionalInfo,
      deadlineInfo,
      overrideJobCover,
      jobApplicationTarget,
      jobExternalTarget,
      jobEmailTarget,
      timeTypes,
      relatedJobs,
    },
    contents,
  } = routeData;

  if (!job) {
    throw new Error('this job does not exist');
  }

  const metaProps = extractPageMetaFromRouteData(routeData);

  const jobSchema = createStructuredData('JobPosting', {
    title: job.title ?? '',
    description: job.jobDescription,
    hiringOrganization: {
      name: 'enercity AG',
      sameAs: 'https://www.enercity.com',
      logo: 'https://enercity.de/assets/logos/enercity_large.png',
    },
    jobLocation: {
      address: {
        addressCountry: job.primaryLocation?.descriptor ?? '',
        addressLocality: job.primaryLocation?.country.descriptor ?? '',
      },
    },
    startDate: job.startDate,
    endDate: job.endDate,
    employmentType: createEmploymentType(timeTypes),
  });

  const jobImage = overrideJobCover || getMatchingJobImage(jobCovers, job);

  useInitialPageDataLayer();

  return (
    <CmsComponentsContext.Provider value={cmsComponents}>
      <Layout variant="default">
        <Page
          meta={{
            ...metaProps,
            ogImage: jobImage,
          }}
        >
          <StructuredData dataType="JobPosting" properties={jobSchema} />

          <JobDetailsPageContent
            jobCovers={jobCovers}
            job={job}
            relatedJobs={relatedJobs}
            timeTypes={timeTypes}
            jobContacts={jobContacts}
            jobBenefits={jobBenefits}
            location={location}
            jobTimeTypeAdditionalInfo={jobTimeTypeAdditionalInfo}
            jobApplicationTarget={jobApplicationTarget}
            jobExternalTarget={jobExternalTarget}
            jobEmailTarget={jobEmailTarget}
            deadlineInfo={deadlineInfo}
            overrideJobCover={overrideJobCover}
            path={routeData.path}
          >
            {contents && <ContentsToReact contents={contents} />}
          </JobDetailsPageContent>
        </Page>
        <EnercityFooter />
      </Layout>
    </CmsComponentsContext.Provider>
  );
};

export default JobDetailsPage;
